import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/cn";
import { Panel } from "./Panel";

/*
 * Khối dựng biểu mẫu theo chuẩn chung (19/09/2026): nhóm theo người dùng, ô rộng theo độ dài dữ liệu trên lưới 12 cột đo theo
 * bề rộng CHÍNH THẺ (container query), gợi ý nằm dưới ô, trường ít dùng gập vào mục "Thêm" tự mở khi có dữ liệu. Dùng ở biểu
 * mẫu sản phẩm và hoá đơn chiếu lệ; trang khác chuyển dần sang.
 */

export type FieldSpan = 3 | 4 | 6 | 8 | 12;

/** Lớp độ rộng — viết nguyên văn để Tailwind thấy. */
export const SPAN_CLASS: Record<FieldSpan, string> = {
  3: "@md:col-span-3", 4: "@md:col-span-4", 6: "@md:col-span-6", 8: "@md:col-span-8", 12: "@md:col-span-12",
};

/** Từ 28rem (448 px) bề rộng thẻ trở lên là lưới 12 cột: đủ cho ba ô mã / số một hàng kể cả khi thẻ nằm nửa màn hình. */
export function FieldGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 gap-x-4 gap-y-4 @md:grid-cols-12">{children}</div>;
}

/** Gợi ý ngắn ngay dưới ô (không đặt ở mép phải nhãn: trên màn rộng nó cách nhãn cả trăm pixel). */
export function Hint({ id, tone = "muted", children }: { id?: string; tone?: "muted" | "warn"; children: React.ReactNode }) {
  return <p id={id} className={cn("mt-1 text-xs", tone === "warn" ? "text-warn" : "text-ink-faint")}>{children}</p>;
}

/** Một nhóm của biểu mẫu: tên nhóm, một câu nói nhóm dùng để làm gì, và nút riêng của nhóm (nếu có). */
export function FormGroup({ id, title, hint, actions, flush = false, children }: {
  id: string;
  title: string;
  hint?: string;
  actions?: React.ReactNode;
  /** Thân thẻ không đệm (bảng dòng hàng cần sát mép). */
  flush?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Panel id={id} role="group" aria-labelledby={`${id}-title`} className="scroll-mt-16">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-b border-line px-4 py-3">
        <div className="min-w-0">
          <h2 id={`${id}-title`} className="text-sm font-semibold text-ink">{title}</h2>
          {hint ? <p className="mt-0.5 text-xs text-ink-soft">{hint}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      <div className={cn("@container", flush ? undefined : "space-y-4 p-4")}>{children}</div>
    </Panel>
  );
}

/**
 * Mục gập "Thêm" của một nhóm: trường ít dùng. `defaultOpen` tính theo dữ liệu LÚC MỞ biểu mẫu (không theo từng phím gõ —
 * xoá ô cuối cùng không làm mục gập lại giữa chừng). `filledLabel`: "n ô có dữ liệu", hiện cạnh tên mục khi có.
 */
export function MoreBlock({ title, filledLabel, defaultOpen, children }: {
  title: string;
  filledLabel?: string | null;
  defaultOpen: boolean;
  children: React.ReactNode;
}) {
  return (
    <details open={defaultOpen} className="group/more rounded-sm border border-line bg-sunken/30">
      <summary className="flex cursor-pointer list-none items-center gap-1.5 px-3 py-2 text-xs font-medium text-ink-soft [&::-webkit-details-marker]:hidden">
        <ChevronRight className="size-3.5 shrink-0 transition-transform group-open/more:rotate-90" aria-hidden="true" />
        {title}
        {filledLabel ? <span className="font-normal text-ink-faint">· {filledLabel}</span> : null}
      </summary>
      <div className="border-t border-line p-3">{children}</div>
    </details>
  );
}

/** Thanh nhảy nhanh tới từng nhóm, dính đầu vùng cuộn. */
export function GroupNav({ label, groups }: { label: string; groups: { id: string; title: string }[] }) {
  return (
    <nav aria-label={label} className="sticky top-0 z-10 -mx-1 flex gap-1.5 overflow-x-auto bg-paper/95 px-1 py-2 backdrop-blur-sm">
      {groups.map((g) => (
        <button
          key={g.id}
          type="button"
          className="shrink-0 rounded-full border border-line bg-surface px-3 py-1 text-xs font-medium text-ink-soft transition-colors hover:border-brand hover:text-brand"
          onClick={() => document.getElementById(g.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
        >
          {g.title}
        </button>
      ))}
    </nav>
  );
}
